import { requestJSON } from "lib/api/base";
import { GetServerSidePropsContext } from "next";
import {
  ClassroomAffiliateSchema,
  PrivateClassroomAffiliateSchema,
  mapKeysSnakeCase,
  SchoolSchema,
  SnakeToCamelCaseNested,
  UserClassroomSchema,
  AffiliateCodeSchema,
} from "@literati/kids-api";

const BASE_URL = "/api/affiliates";

export type ClassroomAffiliate =
  SnakeToCamelCaseNested<ClassroomAffiliateSchema>;

export type PrivateClassroomAffiliate =
  SnakeToCamelCaseNested<PrivateClassroomAffiliateSchema>;

export type Classroom = SnakeToCamelCaseNested<UserClassroomSchema>;
export type School = SnakeToCamelCaseNested<SchoolSchema>;

/**
 * Retrieves school data by affiliate id lookup.
 *
 * @note - this function was built for /schoolbox initially, and
 *         uses the `extAffiliateCode` url query param as the argument
 *
 * @param affiliateId - uid or short code
 */
export async function getSchoolByAffiliateId({
  affiliateId,
  ctx,
}: {
  affiliateId: string;
  ctx?: GetServerSidePropsContext;
}): Promise<School> {
  return await requestJSON(
    `${BASE_URL}/get-school/${affiliateId}`,
    null,
    "GET",
    { ctx }
  );
}

/**
 * Gets a page of affiliate schools.
 * @note - implemented 03/22 for book fair integration
 * @note - paging enforced by the API - response returns link to next call
 *
 * @param ctx
 * @param options
 * @param options.limit
 * @param options.page
 */
export async function getAffiliateSchoolsByPage(
  ctx?: GetServerSidePropsContext,
  options?: any
) {
  const { limit, page } = options || {};
  return await requestJSON(
    `${BASE_URL}/schools${limit ? `?limit=${limit}` : ""}${
      page ? `&page=${page}` : ""
    }`,
    null,
    "GET",
    { ctx }
  );
}

export type AffiliateCode = SnakeToCamelCaseNested<AffiliateCodeSchema>;

/**
 * Gets all affiliate schools by iterating through the pages.
 * @note - pages are fetched serially
 *
 * @param ctx
 * @param options
 * @param options.limit
 */
export async function getAffiliateSchools(
  ctx?: GetServerSidePropsContext,
  options?: any
): Promise<AffiliateCode[]> {
  let response = await getAffiliateSchoolsByPage(ctx, options);
  let schools = response?.results;

  while (response?.next) {
    const [_, next] = response?.next?.split(BASE_URL) || [];

    if (!next) {
      break;
    }

    response = await requestJSON(`${BASE_URL}${next}`, null, "GET", { ctx });
    schools = [...schools, ...response?.results];
  }

  return schools;
}

export type UserClassroom = SnakeToCamelCaseNested<UserClassroomSchema>;

export async function addClassroom(payload: UserClassroom) {
  return await requestJSON(
    `${BASE_URL}/add-classroom`,
    mapKeysSnakeCase(payload),
    "post"
  );
}

export type GetSearchForSchoolResponse = {
  count: number;
  next?: string;
  results?: Array<School>;
};

export async function searchForSchool(
  query: string,
  page = 1,
  limit = 10,
  withDefaultECAffiliate = 1,
  ctx?: GetServerSidePropsContext
): Promise<GetSearchForSchoolResponse> {
  const response = (await requestJSON(
    `${BASE_URL}/school-search`,
    { query, page, limit, with_default_ec_affiliate: withDefaultECAffiliate },
    "get",
    { ctx }
  )) as GetSearchForSchoolResponse;

  return response;
}

export async function getClassrooms(ctx: GetServerSidePropsContext) {
  return await requestJSON(`${BASE_URL}/classrooms`, undefined, "get", { ctx });
}

export async function getClassroom(
  affiliateCode: string,
  ctx?: GetServerSidePropsContext
) {
  return await requestJSON(
    `${BASE_URL}/classrooms/${affiliateCode}`,
    undefined,
    "get",
    { ctx }
  );
}
